import { useCallback } from 'react';

export interface NavigationContext {
  previousUrl: string;
  previousLabel: string;
  metadata?: Record<string, any>;
  timestamp?: number;
}

export interface UseNavigationContextOptions {
  contextType: string;
  autoSave?: boolean;
  ttl?: number; // Time-to-live in milliseconds
}

/**
 * Generic hook for tracking navigation context
 * Can be reused for settings, modals, wizards, multi-step forms, etc.
 *
 * @param options Configuration for the context tracking
 * @returns Object with save, update, get, and clear methods
 *
 * @example
 * const { saveContext, getContext } = useNavigationContext({
 *   contextType: 'settings',
 *   ttl: 24 * 60 * 60 * 1000, // 24 hours
 * });
 */
export function useNavigationContext(options: UseNavigationContextOptions) {
  const { contextType, ttl } = options;
  const storageKey = `ptf-nav-context-${contextType}`;

  // Check if context has expired
  const isExpired = useCallback(
    (context: NavigationContext): boolean => {
      if (!ttl || !context.timestamp) return false;
      return Date.now() - context.timestamp > ttl;
    },
    [ttl],
  );

  // Save context to localStorage
  const saveContext = useCallback(
    (context: Omit<NavigationContext, 'timestamp'>) => {
      if (typeof window === 'undefined') return;

      const contextWithTimestamp: NavigationContext = {
        ...context,
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(storageKey, JSON.stringify(contextWithTimestamp));
      } catch (error) {
        console.warn(
          `Failed to save navigation context for ${contextType}:`,
          error,
        );
      }
    },
    [storageKey, contextType],
  );

  // Update existing context (merge with existing data)
  const updateContext = useCallback(
    (updates: Partial<NavigationContext>) => {
      if (typeof window === 'undefined') return;

      const existing = getContext();
      if (!existing) return;

      const updated: NavigationContext = {
        ...existing,
        ...updates,
        metadata: {
          ...existing.metadata,
          ...updates.metadata,
        },
        timestamp: Date.now(),
      };

      try {
        localStorage.setItem(storageKey, JSON.stringify(updated));
      } catch (error) {
        console.warn(
          `Failed to update navigation context for ${contextType}:`,
          error,
        );
      }
    },
    [storageKey, contextType],
  );

  // Retrieve context from localStorage
  const getContext = useCallback((): NavigationContext | null => {
    if (typeof window === 'undefined') return null;

    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;

      const context: NavigationContext = JSON.parse(stored);

      // Check expiration
      if (isExpired(context)) {
        clearContext();
        return null;
      }

      return context;
    } catch (error) {
      console.warn(
        `Failed to retrieve navigation context for ${contextType}:`,
        error,
      );
      return null;
    }
  }, [storageKey, isExpired]);

  // Clear context from localStorage
  const clearContext = useCallback(() => {
    if (typeof window === 'undefined') return;

    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.warn(
        `Failed to clear navigation context for ${contextType}:`,
        error,
      );
    }
  }, [storageKey, contextType]);

  return {
    saveContext,
    updateContext,
    getContext,
    clearContext,
    isExpired: (context: NavigationContext) => isExpired(context),
  };
}
